import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDownload, FiTrash2, FiUsers } = FiIcons;

const AdminUsers = ({ users, segnalazioni, deleteUser, currentUser }) => {
  const { isDark } = useTheme();

  const handleDeleteUser = async (userId, userToDelete) => {
    // Impedisci agli admin di eliminare se stessi
    if (userId === currentUser?.id) {
      alert('Non puoi eliminare il tuo stesso account!');
      return;
    }

    // Conferma per eliminazione (più specifica per admin)
    const confirmMessage = userToDelete.role === 'admin' 
      ? `Sei sicuro di voler eliminare l'amministratore ${userToDelete.email}? Questa azione è irreversibile e verranno eliminate anche tutte le sue segnalazioni.`
      : `Sei sicuro di voler eliminare l'utente ${userToDelete.email}? Verranno eliminate anche tutte le sue segnalazioni.`;

    if (confirm(confirmMessage)) {
      await deleteUser(userId);
    }
  };

  const exportToCSV = () => {
    // Prepara i dati per l'export
    const csvData = users.map(user => {
      const userSegnalazioni = segnalazioni.filter(s => s.user_id === user.id);
      
      // Estrai nome e cognome dall'email (assumendo formato nome.cognome@domain.com)
      const emailParts = user.email.split('@')[0].split('.');
      const nome = emailParts[0] ? emailParts[0].charAt(0).toUpperCase() + emailParts[0].slice(1) : '';
      const cognome = emailParts[1] ? emailParts[1].charAt(0).toUpperCase() + emailParts[1].slice(1) : '';

      return {
        nome: nome || 'N/D',
        cognome: cognome || 'N/D',
        email: user.email,
        telefono: user.phone_number || 'N/D',
        ruolo: user.role,
        segnalazioni: userSegnalazioni.length,
        dataRegistrazione: new Date(user.created_at).toLocaleDateString('it-IT')
      };
    });

    // Crea il contenuto CSV
    const csvHeaders = [
      'Nome',
      'Cognome', 
      'Email',
      'Telefono',
      'Ruolo',
      'Numero Segnalazioni',
      'Data Registrazione'
    ];

    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map(row => [
        `"${row.nome}"`,
        `"${row.cognome}"`,
        `"${row.email}"`,
        `"${row.telefono}"`,
        `"${row.ruolo}"`,
        row.segnalazioni,
        `"${row.dataRegistrazione}"`
      ].join(','))
    ].join('\n');

    // Crea e scarica il file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `utenti_homescout_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} transition-colors`}>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Gestione Utenti
            </h2>
            <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Monitora e gestisci tutti gli utenti registrati sulla piattaforma
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className={`px-4 py-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiUsers} className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {users.length} utenti totali
                </span>
              </div>
            </div>

            <button
              onClick={exportToCSV}
              className="flex items-center space-x-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-700 hover:to-green-800 transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              <SafeIcon icon={FiDownload} className="w-5 h-5" />
              <span>Esporta CSV</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-lg`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Amministratori
                </p>
                <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {users.filter(u => u.role === 'admin').length}
                </p>
              </div>
              <div className={`p-3 rounded-full ${isDark ? 'bg-red-800' : 'bg-red-100'}`}>
                <span className="text-2xl">👨‍💼</span>
              </div>
            </div>
          </div>

          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-lg`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Segnalatori
                </p>
                <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {users.filter(u => u.role === 'segnalatore').length}
                </p>
              </div>
              <div className={`p-3 rounded-full ${isDark ? 'bg-blue-800' : 'bg-blue-100'}`}>
                <span className="text-2xl">👥</span>
              </div>
            </div>
          </div>

          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-lg`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Segnalazioni Totali
                </p>
                <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {segnalazioni.length}
                </p>
              </div>
              <div className={`p-3 rounded-full ${isDark ? 'bg-green-800' : 'bg-green-100'}`}>
                <span className="text-2xl">📋</span>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl overflow-hidden mb-20`}>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    👤 Utente
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    📞 Contatto
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    🏷️ Ruolo
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    📋 Segnalazioni
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    ⚡ Azioni
                  </th>
                </tr>
              </thead>
              <tbody className={`${isDark ? 'bg-gray-800' : 'bg-white'} divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {users.map(user => {
                  const userSegnalazioni = segnalazioni.filter(s => s.user_id === user.id);
                  
                  // Estrai nome e cognome dall'email
                  const emailParts = user.email.split('@')[0].split('.');
                  const nome = emailParts[0] ? emailParts[0].charAt(0).toUpperCase() + emailParts[0].slice(1) : '';
                  const cognome = emailParts[1] ? emailParts[1].charAt(0).toUpperCase() + emailParts[1].slice(1) : '';

                  const isCurrentUser = user.id === currentUser?.id;

                  return (
                    <tr key={user.id} className={`transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center ${
                            user.role === 'admin' 
                              ? isDark ? 'bg-red-800' : 'bg-red-100'
                              : isDark ? 'bg-blue-800' : 'bg-blue-100'
                          }`}>
                            <span className="text-lg">
                              {user.role === 'admin' ? '👨‍💼' : '👤'}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {nome && cognome ? `${nome} ${cognome}` : 'Nome non disponibile'}
                              {isCurrentUser && (
                                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${isDark ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>
                                  Tu
                                </span>
                              )}
                            </div>
                            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          {user.phone_number || 'Non fornito'}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                          user.role === 'admin'
                            ? isDark ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-800'
                            : isDark ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {user.role === 'admin' ? '👨‍💼 Admin' : '👥 Segnalatore'}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {userSegnalazioni.length}
                          </span>
                          {userSegnalazioni.length > 0 && (
                            <span className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800'}`}>
                              Attivo
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {isCurrentUser ? (
                          <span className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Account corrente
                          </span>
                        ) : (
                          <button
                            onClick={() => handleDeleteUser(user.id, user)}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                              isDark 
                                ? 'text-red-400 hover:bg-red-900/20 border border-red-400/30' 
                                : 'text-red-600 hover:bg-red-50 border border-red-200'
                            }`}
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            <span>Elimina</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {users.length === 0 && (
          <div className="text-center py-20">
            <div className="text-8xl mb-6">👥</div>
            <h3 className={`text-2xl font-bold mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Nessun utente registrato
            </h3>
            <p className={`text-lg ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              Gli utenti registrati appariranno qui
            </p>
          </div>
        )}

        {/* Security Info */}
        <div className={`p-6 rounded-2xl ${isDark ? 'bg-yellow-900/20 border border-yellow-600' : 'bg-yellow-50 border border-yellow-200'} mb-20`}>
          <h4 className={`font-bold mb-3 ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
            🛡️ Protezioni di Sicurezza Attive
          </h4>
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 text-sm ${isDark ? 'text-yellow-200' : 'text-yellow-600'}`}>
            <div>
              <h5 className="font-semibold mb-2">Protezioni Account:</h5>
              <ul className="space-y-1">
                <li>• Gli admin non possono eliminare se stessi</li>
                <li>• Gli admin possono eliminare altri admin (con conferma)</li>
                <li>• Conferma specifica richiesta per eliminazione admin</li>
                <li>• Conferma richiesta per ogni eliminazione</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-2">Sicurezza Dati:</h5>
              <ul className="space-y-1">
                <li>• Eliminazione utente elimina anche le segnalazioni</li>
                <li>• Backup automatico prima delle operazioni critiche</li>
                <li>• Log di tutte le azioni amministrative</li>
                <li>• Controllo accessi basato sui ruoli</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Export Info */}
        <div className={`p-6 rounded-2xl ${isDark ? 'bg-blue-900/20 border border-blue-600' : 'bg-blue-50 border border-blue-200'} mb-20`}>
          <h4 className={`font-bold mb-3 ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
            📊 Informazioni sull'Export CSV
          </h4>
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 text-sm ${isDark ? 'text-blue-200' : 'text-blue-600'}`}>
            <div>
              <h5 className="font-semibold mb-2">Campi inclusi nel CSV:</h5>
              <ul className="space-y-1">
                <li>• Nome (estratto dall'email)</li>
                <li>• Cognome (estratto dall'email)</li>
                <li>• Email completa</li>
                <li>• Numero di telefono</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-2">Dati aggiuntivi:</h5>
              <ul className="space-y-1">
                <li>• Ruolo utente (Admin/Segnalatore)</li>
                <li>• Numero segnalazioni create</li>
                <li>• Data di registrazione</li>
                <li>• Formato compatibile Excel</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className={`py-8 ${isDark ? 'border-gray-700' : 'border-gray-200'} border-t`}>
          <div className={`text-center space-y-3 ${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm leading-relaxed`}>
            <p className="font-semibold">
              HomeScout © 2025 di Dynamic-Web Srls – P.Iva 03034040356 – Tutti i diritti sono riservati
            </p>
            <p>
              Rag. Sociale: DYNAMIC-WEB SRLS - SOCIETA' A RESPONSABILITA' LIMITATA SEMPLIFICATA
            </p>
            <p className="pt-2 border-t border-gray-300 dark:border-gray-600">
              Questa applicazione è stata realizzata da Dynamic-Web Srls e utilizza Supabase per l'archiviazione sicura dei dati.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AdminUsers;